import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  type ReactNode
} from 'react';

type Measurement = (height: number) => void;

/**
 * A view owns one observer; cells register only their settled content. Measurements
 * stay separate from row virtualization and never trigger React renders.
 */
class AiSummaryCellHeightObserver {
  private readonly targets = new Map<Element, Measurement>();
  private observer: ResizeObserver | undefined;
  private connected = false;

  connect(): void {
    this.connected = true;
    if (this.targets.size) {
      this.observeTargets();
    }
  }

  disconnect(): void {
    this.connected = false;
    this.observer?.disconnect();
    this.observer = undefined;
  }

  private observeTargets(): void {
    if (this.observer) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      if (this.observer !== observer) {
        return;
      }
      for (const entry of entries) {
        const height = entry.borderBoxSize[0]?.blockSize;
        if (height !== undefined) {
          this.targets.get(entry.target)?.(height);
        }
      }
    });
    this.observer = observer;
    for (const target of this.targets.keys()) {
      observer.observe(target, { box: 'border-box' });
    }
  }

  register(target: Element, measure: Measurement): () => void {
    this.targets.set(target, measure);
    if (this.connected) {
      if (this.observer) {
        this.observer.observe(target, { box: 'border-box' });
      } else {
        this.observeTargets();
      }
    }
    return () => {
      if (this.targets.get(target) === measure) {
        this.targets.delete(target);
        this.observer?.unobserve(target);
      }
    };
  }
}

const MeasurementContext = createContext<AiSummaryCellHeightObserver | null>(null);

export function AiSummaryCellHeightObserverProvider({
  children
}: {
  children: ReactNode;
}) {
  const [measurements] = useState(() => new AiSummaryCellHeightObserver());
  useLayoutEffect(() => {
    measurements.connect();
    return () => measurements.disconnect();
  }, [measurements]);
  return (
    <MeasurementContext.Provider value={measurements}>
      {children}
    </MeasurementContext.Provider>
  );
}

export const useAiSummaryCellHeightObserver = () => useContext(MeasurementContext);
